import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DOCTOR_QUESTION_MEDICAL_ACTION_LABELS,
  DOCTOR_QUESTION_MEDICAL_ACTIONS,
  type DoctorQuestionMedicalAction,
  type DoctorQuestionSummary,
  type WorkQueueItem,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { PatientLabel } from "@/components/PatientLabel";
import {
  fetchDoctorQuestions,
  markDoctorQuestionHandled,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { answerWaitingText, stockholmDateTime } from "@/lib/medical-question";
import { medicalActionHref } from "@/lib/patient-links";
import { DoctorQuestionDetail } from "./DoctorQuestions";
import { SendDoctorNotice } from "./SendDoctorNotice";
import { useReadOnly, useViewPath } from "./SupportView";

// TES-1332 (TES-1331, admin decision 2026-10-13, "ARBETSKÖ FÖR SVAR PÅ
// LÄKARENS FRÅGA") — the queue is where the doctor reads, judges and closes
// her answer without opening the patient: "Hantera svar" expands the
// original notice, the question, her full answer, who asked, when it was
// published and answered, and how long it has waited. In the row:
//   - "Hanterat – inget nytt besked behövs" (Hanterad, audited);
//   - "Skicka nytt besked": the composer in place, in either mode; the
//     earlier question and answer stay as they were;
//   - "Öppna patientbilden", the secondary way to the wider context.
//
//   - TES-1335: "Starta medicinsk åtgärd", direct links to "Begär
//     komplettering", "Föreslå vårdmöte", "Öppna receptbedömning", "Öppna
//     behandlingsplan" and "Registrera medicinskt ärende", opening that
//     function on the patient page, never the bare page. TES-1400 (Ö-576,
//     Emelie 2026-10-15): "Ett patientsvar blir Hanterat först när läkaren
//     markerat det hanterat eller skapat den spårbara åtgärd som svaret
//     leder till." Choosing one closes nothing: the patient page records the
//     action on her answer (Hanterad) when the action is created there, and
//     abandoning it leaves the answer in the queue.
// Opening the row or the patient closes nothing.
//
// TES-1335 — the safety path: a safety signal on her answer
// (DoctorQuestionSummary.safetySignal) is shown above the actions and
// "Hanterat" is unavailable while it stands. No rule marks patient free
// text yet, so the server always sends null and nothing is shown.
//
// The decision's words: "Hantera svar", "Hanterat – inget nytt besked
// behövs", "Skicka nytt besked", "Öppna patientbilden", "Starta medicinsk
// åtgärd" and the five actions. The rest is proposed copy.
const BTN =
  "rounded-md border border-surfaceMuted px-md py-xs text-sm text-textSecondary hover:text-textPrimary";
const BTN_PRIMARY =
  "rounded-md bg-brand px-md py-xs text-sm text-textOnBrand hover:opacity-90 disabled:opacity-50";

export function DoctorAnswerCard({
  item,
  onDisposed,
  onUnauthorized,
}: {
  item: WorkQueueItem;
  onDisposed?: (next: WorkQueueItem | null) => void;
  onUnauthorized?: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const viewPath = useViewPath();
  const navigate = useNavigate();
  const taskId = item.id.replace(/^doctor-question:/, "");
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState<DoctorQuestionSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<"handled" | "new_notice" | null>(null);
  const canAct = claims?.role === "doctor" && !readOnly;

  function fail(e: unknown, fallback: string) {
    if (e instanceof UnauthorizedError) onUnauthorized?.();
    else setError(fallback);
  }

  async function expand() {
    setOpen(true);
    if (question) return;
    setError(null);
    try {
      const res = await fetchDoctorQuestions(item.patientId);
      const found = res.questions.find((q) => q.taskId === taskId) ?? null;
      setQuestion(found);
      if (!found) setError("Frågan kunde inte hittas.");
    } catch (e) {
      fail(e, "Kunde inte hämta svaret.");
    }
  }

  async function markHandled() {
    setBusy(true);
    setError(null);
    try {
      await markDoctorQuestionHandled(item.patientId, taskId);
      setDone("handled");
      onDisposed?.(null);
    } catch (e) {
      fail(
        e,
        "Svaret kunde inte markeras som hanterat. Ärendet är kvar i kön.",
      );
    } finally {
      setBusy(false);
    }
  }

  function startAction(action: DoctorQuestionMedicalAction) {
    navigate(viewPath(medicalActionHref(item.patientId, action, taskId)));
  }

  if (done) {
    return (
      <div className="rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-md text-sm text-textSecondary">
        {done === "handled"
          ? "Svaret är markerat som hanterat."
          : "Det nya beskedet är publicerat och svaret är hanterat."}
      </div>
    );
  }

  const answeredAt = item.stateSince ?? null;

  return (
    <div className="rounded-md border border-surfaceMuted bg-surface">
      <div className="flex flex-wrap items-center gap-sm p-md">
        <span className="rounded-pill border border-brand/40 px-sm py-xs text-xs text-brand">
          Besvarad
        </span>
        <span className="text-sm text-textPrimary">{item.title}</span>
        <PatientLabel
          className="text-sm"
          patientId={item.patientId}
          name={item.patientName}
          personalNumberLast6={item.personalNumberLast6}
          link
        />
        {answeredAt ? (
          <span className="text-xs text-textSecondary">
            Svar {stockholmDateTime(answeredAt)} ·{" "}
            {answerWaitingText(answeredAt, new Date())}
          </span>
        ) : null}
        <div className="ml-auto flex gap-sm">
          {open ? (
            <button
              type="button"
              className={BTN}
              onClick={() => setOpen(false)}
            >
              Fäll ihop
            </button>
          ) : (
            <button
              type="button"
              className={BTN_PRIMARY}
              onClick={() => void expand()}
            >
              Hantera svar
            </button>
          )}
        </div>
      </div>

      {open ? (
        <div className="flex flex-col gap-sm border-t border-surfaceMuted p-md text-sm">
          {question ? (
            <DoctorQuestionDetail question={question} noticeOpen />
          ) : !error ? (
            <p className="text-textSecondary">Hämtar…</p>
          ) : null}
          {error ? (
            <p className="text-xs text-danger" role="alert">
              {error}
            </p>
          ) : null}
          {question?.safetySignal ? (
            <div
              className="rounded-md border border-danger/40 bg-danger/10 p-sm text-sm text-danger"
              role="alert"
            >
              <p className="font-medium">{question.safetySignal.label}</p>
              <p className="text-xs">
                Följ säkerhetsvägen innan svaret kan markeras som hanterat.
              </p>
            </div>
          ) : null}
          {question && canAct ? (
            <div className="flex flex-col gap-sm">
              <div className="flex flex-wrap gap-sm">
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  disabled={busy || question.safetySignal !== null}
                  onClick={() => void markHandled()}
                >
                  {busy ? "Sparar…" : "Hanterat – inget nytt besked behövs"}
                </button>
                <Link
                  to={viewPath(`/patients/${item.patientId}`)}
                  className={BTN}
                >
                  Öppna patientbilden
                </Link>
              </div>
              <div className="flex flex-col gap-xs">
                <p className="text-sm font-medium">Starta medicinsk åtgärd</p>
                <p className="text-xs text-textSecondary">
                  Svaret markeras som hanterat när åtgärden är skapad, och
                  frågan, svaret och åtgärden förblir kopplade. Avbryter du
                  ligger svaret kvar i kön.
                </p>
                <div className="flex flex-wrap gap-sm">
                  {DOCTOR_QUESTION_MEDICAL_ACTIONS.map((action) => (
                    <button
                      key={action}
                      type="button"
                      className={BTN}
                      disabled={busy}
                      onClick={() => startAction(action)}
                    >
                      {DOCTOR_QUESTION_MEDICAL_ACTION_LABELS[action]}
                    </button>
                  ))}
                </div>
              </div>
              <SendDoctorNotice
                patientId={item.patientId}
                onUnauthorized={() => onUnauthorized?.()}
                followUpOf={{ taskId }}
                onSent={() => {
                  setDone("new_notice");
                  onDisposed?.(null);
                }}
              />
            </div>
          ) : question ? (
            <Link
              to={viewPath(`/patients/${item.patientId}`)}
              className={`${BTN} self-start`}
            >
              Öppna patientbilden
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
